import {
  AlertCircle,
  Calendar,
  CheckSquare,
  ChevronRight,
  Database,
  Edit2,
  FileText,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCw,
  Sparkles,
  XSquare,
} from "lucide-react";
import * as React from "react";

import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { Badge, Button, cn } from "@ando/ui";

import type {
  ApproveSuggestionRequest,
  WorkflowSuggestionDetail,
  WorkflowSuggestionItem,
} from "@ando/core";

import {
  useApproveWorkflowSuggestion,
  useRejectWorkflowSuggestion,
  useWorkflowSuggestionDetail,
  useWorkflowSuggestions,
} from "@/features/admin/hooks/useAdminHypermem";
import { formatInt, formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmLoading } from "./HmStates";

/**
 * HmWorkflowSuggestionsTab (v2 port, Jun 6, 2026) — MEDIUM PII SENSITIVITY.
 *
 * Admin governance UI for Process Codification: the agent persists recurring
 * action patterns into ``pending_workflow_suggestions`` (status=pending). This
 * tab lets the admin:
 *   1. Inspect each suggestion (signature + occurrences + last_seen)
 *   2. Expand an inline panel with sample facts + blueprint preview JSON
 *   3. Edit name / prompt before materialisation
 *   4. Approve (creates a Temporal automation) or reject with a reason
 *
 * Privacy posture: sample facts can carry raw PII (e.g. recipient emails). The
 * admin already cleared the role gate + privacy banner upstream; the BE is the
 * source of truth (Art. 32 GDPR defense-in-depth) and this is an admin-only
 * path, so no client-side redaction is applied here.
 *
 * v2 conventions: TanStack Query hooks from ``useAdminHypermem``, ``@ando/ui``
 * primitives (Badge / Button / cn), lucide-react icons, ando Tailwind tokens.
 * Detail is an inline expandable panel (no Dialog dependency assumed).
 */

// ── Constants ──────────────────────────────────────────────────────────────

type SuggestionStatus = "pending" | "approved" | "rejected" | "superseded";

const STATUS_OPTIONS: ReadonlyArray<{
  value: SuggestionStatus;
  label: MessageDescriptor;
}> = [
  { value: "pending", label: msg`In attesa` },
  { value: "approved", label: msg`Approvati` },
  { value: "rejected", label: msg`Rigettati` },
  { value: "superseded", label: msg`Superati` },
];

const WEEKDAY_IT: Record<string, MessageDescriptor> = {
  monday: msg`lunedì`,
  tuesday: msg`martedì`,
  wednesday: msg`mercoledì`,
  thursday: msg`giovedì`,
  friday: msg`venerdì`,
  saturday: msg`sabato`,
  sunday: msg`domenica`,
};

const ACTION_TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  send_email: Mail,
  send_message: MessageSquare,
  document_create: FileText,
  document_edit: Edit2,
  propose_db_write: Database,
  create_event: Calendar,
};

// ── Helpers ──────────────────────────────────────────────────────────────

function formatCadence(i18n: I18n, cadence: string): string {
  if (!cadence) return "—";
  if (cadence === "daily") return i18n._(msg`Ogni giorno alle 09:00`);
  if (cadence === "monthly") return i18n._(msg`Il 1° di ogni mese alle 09:00`);
  if (cadence === "ad_hoc") return i18n._(msg`Occasionalmente`);
  if (cadence.startsWith("weekly_")) {
    const day = cadence.split("_")[1] ?? "";
    const it = WEEKDAY_IT[day];
    return it ? i18n._(msg`Ogni ${i18n._(it)} alle 09:00`) : i18n._(msg`Settimanale (${day})`);
  }
  return cadence;
}

function actionTypeIcon(actionType: string): React.ComponentType<{ className?: string }> {
  return ACTION_TYPE_ICON[actionType] ?? Sparkles;
}

function prettyActionType(actionType: string): string {
  if (!actionType) return "—";
  return actionType.replace(/_/g, " ");
}

function occurrenceVariant(n: number): React.ComponentProps<typeof Badge>["variant"] {
  if (n >= 5) return "success";
  if (n >= 3) return "accent";
  return "outline";
}

/** Pull the most likely prompt-ish string out of a blueprint template. */
function blueprintPrompt(bp: Record<string, unknown> | undefined): string {
  if (!bp) return "";
  const candidate =
    (typeof bp.prompt === "string" && bp.prompt) ||
    (typeof bp.user_prompt === "string" && bp.user_prompt) ||
    (typeof bp.description === "string" && bp.description) ||
    "";
  return candidate || "";
}

/** Pull the most readable text + a stable id out of an arbitrary fact record. */
function factText(fact: Record<string, unknown>): string {
  return (
    (typeof fact.fact_text === "string" && fact.fact_text) ||
    (typeof fact.text === "string" && fact.text) ||
    (typeof fact.summary === "string" && fact.summary) ||
    JSON.stringify(fact)
  );
}

function factId(fact: Record<string, unknown>, idx: number): string {
  return (
    (typeof fact.fact_id === "string" && fact.fact_id) ||
    (typeof fact.id === "string" && fact.id) ||
    `fact-${idx}`
  );
}

// ── Small presentational pieces ──────────────────────────────────────────

function SignaturePreview({
  signature,
}: {
  signature: WorkflowSuggestionItem["signature"];
}): React.ReactElement {
  const Icon = actionTypeIcon(signature.action_type);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-accent" aria-hidden />
      <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs">
        <Badge variant="outline" className="font-mono">
          {prettyActionType(signature.action_type)}
        </Badge>
        <ChevronRight className="h-3 w-3 text-ink-3" aria-hidden />
        <Badge variant="outline" className="font-mono">
          {signature.target_kind}
        </Badge>
        <ChevronRight className="h-3 w-3 text-ink-3" aria-hidden />
        <Badge variant="accent" className="font-mono">
          {signature.temporal_cadence}
        </Badge>
      </div>
    </div>
  );
}

function EmptyState(): React.ReactElement {
  const { i18n } = useLingui();
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center text-ink-3 text-sm">
      <Sparkles className="mb-2 h-8 w-8 text-accent/60" aria-hidden />
      <p className="font-medium text-ink-1">
        {i18n._(msg`Nessun suggerimento di workflow al momento`)}
      </p>
      <p className="mt-1 max-w-sm text-xs">
        {i18n._(
          msg`Ando li proporrà quando rileverà pattern ricorrenti nelle tue azioni (≥3 occorrenze in finestra 30 giorni).`,
        )}
      </p>
    </div>
  );
}

function ErrorState({ message }: { message: string }): React.ReactElement {
  const { i18n } = useLingui();
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-xl border border-red-300/70 bg-transparent p-4 text-red-700 text-sm"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>
        <p className="font-medium">{i18n._(msg`Impossibile caricare i workflow suggestions`)}</p>
        <p className="mt-0.5 text-xs opacity-80">{message}</p>
      </div>
    </div>
  );
}

// ── Detail panel (inline expandable) ───────────────────────────────────────

interface DetailPanelProps {
  item: WorkflowSuggestionItem;
  detail: WorkflowSuggestionDetail | undefined;
  detailLoading: boolean;
  detailError: string | null;
  approvePending: boolean;
  rejectPending: boolean;
  onApprove: (edits: { name: string; prompt: string }) => void;
  onReject: (reason: string) => void;
  onClose: () => void;
}

function DetailPanel({
  item,
  detail,
  detailLoading,
  detailError,
  approvePending,
  rejectPending,
  onApprove,
  onReject,
  onClose,
}: DetailPanelProps): React.ReactElement {
  const { i18n } = useLingui();
  const [editedName, setEditedName] = React.useState(item.suggested_workflow_name);
  const [editedPrompt, setEditedPrompt] = React.useState("");
  const [rejectMode, setRejectMode] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState("");

  // Seed name when a new item is selected.
  React.useEffect(() => {
    setEditedName(item.suggested_workflow_name);
    setEditedPrompt("");
    setRejectMode(false);
    setRejectReason("");
  }, [item.id, item.suggested_workflow_name]);

  // Seed prompt from blueprint once detail lands.
  React.useEffect(() => {
    if (detail) setEditedPrompt(blueprintPrompt(detail.blueprint_template));
  }, [detail]);

  const submitting = approvePending || rejectPending;
  const cadenceText = formatCadence(i18n, item.signature.temporal_cadence);
  const reasonValid = rejectReason.trim().length > 0;

  return (
    <div className="space-y-4 border-hairline border-t bg-cream/40 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <SignaturePreview signature={item.signature} />
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={occurrenceVariant(item.occurrences)} className="gap-1">
              <RefreshCw className="h-3 w-3" aria-hidden />
              {i18n._(msg`${formatInt(item.occurrences)} occorrenze`)}
            </Badge>
            <span className="text-ink-3 text-xs">
              {i18n._(msg`Ultima vista ${formatTimestamp(item.last_seen)}`)}
            </span>
          </div>
          <p className="text-ink-3 text-xs italic">
            {i18n._(msg`Cadence:`)} <strong className="not-italic">{cadenceText}</strong>
          </p>
          {item.automation_id && (
            <p className="text-ink-2 text-xs">
              {i18n._(msg`Automation:`)}{" "}
              <code className="rounded bg-ink-3/10 px-1 py-0.5 font-mono text-[11px] text-ink-1">
                {item.automation_id}
              </code>
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {i18n._(msg`Chiudi`)}
        </Button>
      </div>

      {/* Detail body */}
      {detailLoading && (
        <div className="flex items-center gap-2 py-6 text-ink-3">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          <span className="text-sm">{i18n._(msg`Caricamento dettaglio…`)}</span>
        </div>
      )}

      {detailError && !detailLoading && <ErrorState message={detailError} />}

      {detail && !detailLoading && !detailError && (
        <>
          {/* Sample facts */}
          <section aria-labelledby={`ws-facts-${item.id}`}>
            <h3
              id={`ws-facts-${item.id}`}
              className="mb-2 flex items-center gap-2 font-semibold text-ink-1 text-sm"
            >
              <FileText className="h-4 w-4 text-ink-3" aria-hidden />
              {i18n._(msg`Sample facts (${detail.sample_facts.length})`)}
            </h3>
            {detail.sample_facts.length === 0 ? (
              <p className="text-ink-3 text-xs italic">
                {i18n._(msg`Nessun sample fact associato.`)}
              </p>
            ) : (
              <ul className="space-y-2">
                {detail.sample_facts.slice(0, 3).map((fact, idx) => {
                  const createdAt = typeof fact.created_at === "string" ? fact.created_at : null;
                  return (
                    <li
                      key={factId(fact, idx)}
                      className="rounded-xl border border-hairline bg-cream/40 p-3 text-xs"
                    >
                      <p className="whitespace-pre-wrap break-words text-ink-1">{factText(fact)}</p>
                      {createdAt && (
                        <p className="mt-1.5 text-[11px] text-ink-3">
                          {formatTimestamp(createdAt)}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Blueprint preview */}
          <section aria-labelledby={`ws-bp-${item.id}`}>
            <h3
              id={`ws-bp-${item.id}`}
              className="mb-2 flex items-center gap-2 font-semibold text-ink-1 text-sm"
            >
              <Database className="h-4 w-4 text-ink-3" aria-hidden />
              {i18n._(msg`Blueprint preview`)}
            </h3>
            <details className="rounded-xl border border-hairline bg-cream/40">
              <summary className="cursor-pointer select-none rounded-xl px-3 py-2 font-medium text-ink-1 text-xs hover:bg-ink-3/5">
                {i18n._(msg`Mostra JSON`)}
              </summary>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words p-3 text-[11px] text-ink-2 leading-snug">
                {JSON.stringify(detail.blueprint_template, null, 2)}
              </pre>
            </details>
          </section>

          {/* Suggested automation editor */}
          <section aria-labelledby={`ws-edit-${item.id}`}>
            <h3
              id={`ws-edit-${item.id}`}
              className="mb-2 flex items-center gap-2 font-semibold text-ink-1 text-sm"
            >
              <Edit2 className="h-4 w-4 text-ink-3" aria-hidden />
              {i18n._(msg`Suggested automation`)}
            </h3>
            <div className="space-y-3">
              <div className="space-y-1">
                <label htmlFor={`ws-name-${item.id}`} className="text-ink-2 text-xs">
                  {i18n._(msg`Nome automation`)}
                </label>
                <input
                  id={`ws-name-${item.id}`}
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder={item.suggested_workflow_name}
                  maxLength={120}
                  className="w-full rounded-xl border border-hairline bg-card px-3 py-2 text-[13px] text-ink-1 outline-none focus:border-ando-orange/60 focus:ring-2 focus:ring-ando-orange/20"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor={`ws-prompt-${item.id}`} className="text-ink-2 text-xs">
                  {i18n._(msg`Prompt (opzionale — override blueprint)`)}
                </label>
                <textarea
                  id={`ws-prompt-${item.id}`}
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                  placeholder={i18n._(msg`Lascia vuoto per usare il prompt del blueprint`)}
                  rows={4}
                  maxLength={2000}
                  className="w-full resize-y rounded-xl border border-hairline bg-card px-3 py-2 text-[13px] text-ink-1 outline-none focus:border-ando-orange/60 focus:ring-2 focus:ring-ando-orange/20"
                />
                <p className="text-[11px] text-ink-3">{editedPrompt.length}/2000</p>
              </div>
            </div>
          </section>

          <p className="text-[11px] text-ink-3 italic">
            {i18n._(
              msg`I sample facts possono contenere PII (es. email destinatari). L'accesso è loggato in audit trail.`,
            )}
          </p>
        </>
      )}

      {/* Reject reason editor */}
      {rejectMode && (
        <div className="space-y-1 rounded-xl border border-hairline bg-card p-3">
          <label htmlFor={`ws-reject-${item.id}`} className="font-medium text-ink-1 text-xs">
            {i18n._(msg`Motivo del rifiuto`)}
          </label>
          <p className="text-[11px] text-ink-3">
            {i18n._(
              msg`Spiega perché rifiuti questo suggerimento — l'audit trail registrerà la motivazione.`,
            )}
          </p>
          <textarea
            id={`ws-reject-${item.id}`}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={i18n._(
              msg`Es: il pattern è coincidente, non rappresenta un workflow ricorrente`,
            )}
            rows={3}
            maxLength={1000}
            required
            aria-label={i18n._(msg`Motivo del rifiuto`)}
            className="w-full resize-y rounded-xl border border-hairline bg-card px-3 py-2 text-[13px] text-ink-1 outline-none focus:border-ando-orange/60 focus:ring-2 focus:ring-ando-orange/20"
          />
          <p className="text-[11px] text-ink-3">
            {i18n._(msg`${rejectReason.trim().length}/1000 · richiesto`)}
          </p>
        </div>
      )}

      {/* Footer actions (only when actionable) */}
      {item.status === "pending" && (
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {rejectMode ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRejectMode(false);
                  setRejectReason("");
                }}
                disabled={submitting}
              >
                {i18n._(msg`Annulla`)}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onReject(rejectReason.trim())}
                disabled={submitting || !reasonValid}
              >
                {rejectPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <XSquare className="mr-1.5 h-4 w-4" aria-hidden />
                )}
                {i18n._(msg`Conferma rifiuto`)}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRejectMode(true)}
                disabled={submitting || !detail}
              >
                <XSquare className="mr-1.5 h-4 w-4" aria-hidden />
                {i18n._(msg`Rigetta`)}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  onApprove({
                    name: editedName.trim() || item.suggested_workflow_name,
                    prompt: editedPrompt.trim(),
                  })
                }
                disabled={submitting || !detail}
              >
                {approvePending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <CheckSquare className="mr-1.5 h-4 w-4" aria-hidden />
                )}
                {i18n._(msg`Approva e crea`)}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function HmWorkflowSuggestionsTab({
  tenantId,
}: {
  tenantId: string;
}): React.ReactElement {
  const { i18n } = useLingui();
  const [statusFilter, setStatusFilter] = React.useState<SuggestionStatus>("pending");
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const {
    data: items,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useWorkflowSuggestions(tenantId, statusFilter);

  const detailQuery = useWorkflowSuggestionDetail(expandedId);
  const approve = useApproveWorkflowSuggestion(tenantId);
  const reject = useRejectWorkflowSuggestion(tenantId);

  const rows = items ?? [];
  const pendingCount = statusFilter === "pending" ? rows.length : null;

  const expandedItem = React.useMemo(
    () => rows.find((r) => r.id === expandedId) ?? null,
    [rows, expandedId],
  );

  // Auto-collapse if the expanded row vanished after a refetch/mutation.
  React.useEffect(() => {
    if (expandedId && !rows.some((r) => r.id === expandedId)) {
      setExpandedId(null);
    }
  }, [rows, expandedId]);

  const toggle = React.useCallback((id: string) => {
    setActionError(null);
    setExpandedId((cur) => (cur === id ? null : id));
  }, []);

  const handleApprove = React.useCallback(
    (id: string, edits: { name: string; prompt: string }) => {
      setActionError(null);
      const original = rows.find((r) => r.id === id);
      const body: ApproveSuggestionRequest = {
        edit_name:
          edits.name && edits.name !== original?.suggested_workflow_name ? edits.name : undefined,
        edit_prompt: edits.prompt ? edits.prompt : undefined,
      };
      approve.mutate(
        { id, body },
        {
          onSuccess: () => setExpandedId(null),
          onError: (err) =>
            setActionError(
              err instanceof Error
                ? i18n._(msg`Approvazione fallita: ${err.message}`)
                : i18n._(msg`Approvazione fallita.`),
            ),
        },
      );
    },
    [approve, rows, i18n],
  );

  const handleReject = React.useCallback(
    (id: string, reason: string) => {
      setActionError(null);
      reject.mutate(
        { id, reason },
        {
          onSuccess: () => setExpandedId(null),
          onError: (err) =>
            setActionError(
              err instanceof Error
                ? i18n._(msg`Rifiuto fallito: ${err.message}`)
                : i18n._(msg`Rifiuto fallito.`),
            ),
        },
      );
    },
    [reject, i18n],
  );

  return (
    <div className="space-y-4">
      {/* Header / filter bar */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="flex items-center gap-2 font-semibold text-base text-ink-1">
            <Sparkles className="h-5 w-5 shrink-0 text-accent" aria-hidden />
            {i18n._(msg`Workflow Suggestions`)}
            {pendingCount != null && pendingCount > 0 && (
              <Badge variant="accent">{i18n._(msg`${pendingCount} in attesa`)}</Badge>
            )}
          </h3>
          <p className="max-w-2xl text-ink-3 text-sm">
            {i18n._(
              msg`Pattern di azioni ripetute rilevati da Process Codification. Approva per creare un Operatore automatizzato Temporal, rigetta per archiviare con motivazione (audit trail).`,
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1.5"
            role="group"
            aria-label={i18n._(msg`Filtra per status`)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setStatusFilter(opt.value);
                  setExpandedId(null);
                  setActionError(null);
                }}
                aria-pressed={statusFilter === opt.value}
                className={cn(
                  "rounded-lg px-3 py-1.5 font-medium text-sm transition-colors",
                  statusFilter === opt.value
                    ? "bg-ando-orange/15 text-ando-orange"
                    : "text-ink-2 hover:bg-ink-3/10",
                )}
              >
                {i18n._(opt.label)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="flex shrink-0 items-center gap-1.5 text-ink-3 text-xs transition-colors hover:text-ink-1"
            aria-label={i18n._(msg`Aggiorna lista`)}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} aria-hidden />
            {i18n._(msg`Aggiorna`)}
          </button>
        </div>
      </div>

      {actionError && <ErrorState message={actionError} />}

      {/* Body */}
      {isLoading ? (
        <HmLoading label={i18n._(msg`Caricamento suggerimenti…`)} />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : i18n._(msg`Errore sconosciuto`)}
        />
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-hairline">
          {rows.map((it, idx) => {
            const isOpen = expandedId === it.id;
            return (
              <li
                key={it.id}
                className={cn(idx > 0 && "border-hairline border-t", isOpen && "bg-cream/40")}
              >
                <button
                  type="button"
                  onClick={() => toggle(it.id)}
                  aria-expanded={isOpen}
                  aria-label={
                    isOpen
                      ? i18n._(msg`Chiudi dettagli per ${it.suggested_workflow_name}`)
                      : i18n._(msg`Apri dettagli per ${it.suggested_workflow_name}`)
                  }
                  className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-ink-3/5"
                >
                  <span className="min-w-0 flex-1">
                    <SignaturePreview signature={it.signature} />
                  </span>
                  <Badge variant={occurrenceVariant(it.occurrences)}>
                    {formatInt(it.occurrences)}×
                  </Badge>
                  <span className="hidden w-40 shrink-0 text-ink-3 text-xs sm:block">
                    {formatTimestamp(it.last_seen)}
                  </span>
                  <span className="hidden min-w-0 flex-1 truncate text-ink-1 text-sm md:block">
                    {it.suggested_workflow_name}
                  </span>
                  {it.status === "approved" && it.automation_id && (
                    <Badge variant="success" className="hidden lg:inline-flex">
                      {i18n._(msg`attiva`)}
                    </Badge>
                  )}
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 shrink-0 text-ink-3 transition-transform",
                      isOpen && "rotate-90",
                    )}
                    aria-hidden
                  />
                </button>

                {isOpen && expandedItem && (
                  <DetailPanel
                    item={expandedItem}
                    detail={detailQuery.data}
                    detailLoading={detailQuery.isLoading}
                    detailError={
                      detailQuery.isError
                        ? detailQuery.error instanceof Error
                          ? detailQuery.error.message
                          : i18n._(msg`Errore sconosciuto`)
                        : null
                    }
                    approvePending={approve.isPending}
                    rejectPending={reject.isPending}
                    onApprove={(edits) => handleApprove(it.id, edits)}
                    onReject={(reason) => handleReject(it.id, reason)}
                    onClose={() => setExpandedId(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default HmWorkflowSuggestionsTab;
